import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Table2, Check } from 'lucide-react';

const ELEMENTS_GRID = [
  { num: 1, sym: 'H', name: 'Hydrogen' }, { num: 2, sym: 'He', name: 'Helium' },
  { num: 3, sym: 'Li', name: 'Lithium' }, { num: 4, sym: 'Be', name: 'Beryllium' },
  { num: 5, sym: 'B', name: 'Boron' }, { num: 6, sym: 'C', name: 'Carbon' },
  { num: 7, sym: 'N', name: 'Nitrogen' }, { num: 8, sym: 'O', name: 'Oxygen' },
  { num: 9, sym: 'F', name: 'Fluorine' }, { num: 10, sym: 'Ne', name: 'Neon' },
  { num: 11, sym: 'Na', name: 'Sodium' }, { num: 12, sym: 'Mg', name: 'Magnesium' },
  { num: 13, sym: 'Al', name: 'Aluminium' }, { num: 14, sym: 'Si', name: 'Silicon' },
  { num: 15, sym: 'P', name: 'Phosphorus' }, { num: 16, sym: 'S', name: 'Sulphur' },
  { num: 17, sym: 'Cl', name: 'Chlorine' }, { num: 18, sym: 'Ar', name: 'Argon' },
  { num: 19, sym: 'K', name: 'Potassium' }, { num: 20, sym: 'Ca', name: 'Calcium' },
];

const PeriodicPuzzle = () => {
  const navigate = useNavigate();
  const [answers, setAnswers] = useState({});
  const [feedback, setFeedback] = useState(null);
  const [score, setScore] = useState(0);
  const [checked, setChecked] = useState(false);

  // Randomly hide 6 elements
  const [hidden] = useState(() => {
    const indices = [];
    while (indices.length < 6) { const i = Math.floor(Math.random() * 20); if (!indices.includes(i)) indices.push(i); }
    return indices;
  });

  const checkAnswers = () => {
    let correct = 0;
    hidden.forEach(idx => {
      const el = ELEMENTS_GRID[idx];
      if (answers[idx]?.toLowerCase().trim() === el.sym.toLowerCase()) correct++;
    });
    setScore(correct * 15);
    setChecked(true);
    setFeedback(correct === hidden.length ? 'Perfect! You know your elements!' : `${correct}/${hidden.length} correct.`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-50" data-testid="periodic-puzzle-game">
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate('/games')} className="flex items-center gap-2 text-[#64748B] font-semibold"><ArrowLeft className="w-5 h-5" />Back</button>
          <span className="bg-[#4D96FF] text-white px-4 py-2 rounded-2xl font-bold">{score}pts</span>
        </div>
        <h2 className="text-3xl font-black text-[#1E293B] text-center mb-2">Fill the Missing Elements!</h2>
        <p className="text-center text-[#64748B] mb-6">Type the correct symbol for each hidden element</p>
        <div className="bg-white rounded-3xl p-6 border-2 border-slate-100 shadow-xl">
          <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
            {ELEMENTS_GRID.map((el, idx) => {
              const isHidden = hidden.includes(idx);
              const isCorrect = checked && isHidden && answers[idx]?.toLowerCase().trim() === el.sym.toLowerCase();
              const isWrong = checked && isHidden && answers[idx]?.toLowerCase().trim() !== el.sym.toLowerCase();
              return (
                <div key={idx} className={`aspect-square rounded-xl border-2 flex flex-col items-center justify-center text-center p-1 transition-all ${
                  isCorrect ? 'bg-green-100 border-green-500' : isWrong ? 'bg-red-100 border-red-500' : isHidden ? 'bg-yellow-50 border-yellow-400' : 'bg-teal-50 border-teal-200'
                }`}>
                  <span className="text-[10px] font-bold text-[#64748B]">{el.num}</span>
                  {isHidden ? (
                    <>
                      <input type="text" value={answers[idx] || ''} onChange={(e) => setAnswers({...answers, [idx]: e.target.value})}
                        className="w-full text-center text-sm font-black bg-transparent outline-none" maxLength={2} disabled={checked}
                        placeholder="?" data-testid={`element-input-${idx}`} />
                      {checked && isWrong && <span className="text-[10px] text-red-500 font-bold">{el.sym}</span>}
                    </>
                  ) : (
                    <>
                      <span className="text-lg font-black text-[#1E293B]">{el.sym}</span>
                      <span className="text-[8px] text-[#64748B] truncate w-full">{el.name}</span>
                    </>
                  )}
                </div>
              );
            })}
          </div>
          {feedback && <div className={`mt-6 p-4 rounded-2xl font-bold text-center ${score === hidden.length * 15 ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>{feedback}</div>}
          <div className="flex gap-3 justify-center mt-6">
            {!checked ? (
              <button onClick={checkAnswers} className="px-8 py-3 bg-teal-500 text-white rounded-2xl font-bold border-b-4 border-teal-700 active:border-b-0 active:translate-y-1 transition-all" data-testid="check-periodic-btn"><Check className="w-5 h-5 inline mr-1" />Check Answers</button>
            ) : (
              <button onClick={() => navigate('/games')} className="px-8 py-3 bg-teal-500 text-white rounded-2xl font-bold">Back to Brain Arena</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PeriodicPuzzle;
